/**
 * Platform Console access gate — DUAL-SURFACE entitlement model.
 *
 * The app has two surfaces: the PRODUCT (workspace/chat) and the Platform
 * CONSOLE. Explicit ``product_access`` flags are authoritative. Old sessions
 * without these flags retain the legacy role mapping until refreshed:
 *
 *   - ``platform-dev``           → console ONLY (the console IS their app)
 *   - ``admin`` / ``superadmin`` → BOTH surfaces
 *   - any other authed role      → product ONLY
 *
 * ``CONSOLE_ACCESS_MOCK`` is a DEV-ONLY escape hatch (env-driven, default
 * OFF now that the console backend is live and roles are real). It never
 * widens access in production builds or under test.
 */

import type { UserInfo } from "@ando/core/auth";

/** Roles entitled to the Platform Console. */
const CONSOLE_ROLES = new Set(["platform-dev", "admin", "superadmin"]);

/** The console-only role — no product surface. */
const CONSOLE_ONLY_ROLE = "platform-dev";
let engineAccessHint = false;

/** UX hint set only after a successful backend entitlement check/activation. */
export function markEngineAccess(): void {
  engineAccessHint = true;
}

export function clearEngineAccessHint(): void {
  engineAccessHint = false;
}

/**
 * DEV-ONLY escape hatch: widens console access to every authed user in
 * local dev. Default OFF — the console backend is live, so dev uses the
 * real role-derived ``surfaceAccess`` like production. Opt in explicitly
 * with ``VITE_CONSOLE_ACCESS_MOCK=true`` (e.g. to work on console pages
 * with a plain ``user`` session). Ignored in production builds and in
 * vitest (``MODE === "test"``) so the role derivation stays testable.
 */
export const CONSOLE_ACCESS_MOCK = import.meta.env.VITE_CONSOLE_ACCESS_MOCK === "true";

/** True when the dev-only mock may widen console access. */
function devConsoleOverrideActive(): boolean {
  return CONSOLE_ACCESS_MOCK && import.meta.env.DEV && import.meta.env.MODE !== "test";
}

/** Which surfaces this session is entitled to enter. */
export interface SurfaceAccess {
  readonly console: boolean;
  readonly product: boolean;
}

/**
 * Derive the surface entitlements for a session (single source of truth —
 * route guards, landing redirects and surface switchers all read THIS).
 */
export function surfaceAccess(session: UserInfo | null): SurfaceAccess {
  if (!session) return { console: false, product: false };
  if (session.product_access) {
    return {
      console: session.product_access.engine === true,
      product: session.product_access.workspace === true,
    };
  }
  const role = session.role ?? "user";
  return {
    console: CONSOLE_ROLES.has(role) || engineAccessHint || devConsoleOverrideActive(),
    product: role !== CONSOLE_ONLY_ROLE,
  };
}

/** Route-level console gate — backed by ``surfaceAccess`` (+ dev mock). */
export function hasConsoleAccess(session: UserInfo | null): boolean {
  return surfaceAccess(session).console;
}

/**
 * In-console CAPABILITY tier — the SECOND, orthogonal gating dimension.
 *
 * ``surfaceAccess`` (above) decides WHICH surfaces the session may enter, from
 * the global role. The capability tier decides what a session may DO once
 * inside the console (``owner``/``admin`` = "Gestore" → the Account group =
 * Membri + Fatturazione; ``member`` = "Sviluppatore" → Build/Develop/Observe
 * only). That rule is computed SERVER-SIDE — ``GET /context`` returns
 * ``can_manage`` (the single source of truth, ``console.context``); the nav
 * filter (``ConsoleShell``) and the Account-group route guards read that
 * boolean directly, so it is never re-derived on the client.
 */
